import { useEffect, useRef, useState } from 'react';
import { MessageSquareText, X, Send, Loader2, Mail } from 'lucide-react';
import { AttachButton } from './AttachButton';
import { useVisitorChat } from './useVisitorChat';
import { ChatBubble, TypingNote } from './ChatBubble';
import { ContactCard } from './ContactCard';
import { PreChatForm } from './PreChatForm';


export default function LiveChat() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [hideCard, setHideCard] = useState(false);
  const [note, setNote] = useState('');
  const [office, setOffice] = useState(null);
  useEffect(() => { if (open && !office) fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/config`).then((r) => r.json()).then((c) => setOffice(c.office_status)).catch(() => {}); }, [open, office]);
  const { messages, meta, unread, send, saveDetails, started, session, fileHref, uploadUrl, addMessage, emailTranscript, typing } = useVisitorChat(open);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const talk = messages.filter((m) => m.sender !== 'system');
  const waiting = talk.length > 0 && talk[talk.length - 1].sender === 'visitor';
  const showCard = started && !meta.has_contact && !hideCard && talk.some((m) => m.sender !== 'visitor');

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length, open, waiting]);
  useEffect(() => { if (open && started) inputRef.current?.focus(); }, [open, started]);
  useEffect(() => { const t = window.Tawk_API; if (t?.hideWidget) { if (open) t.hideWidget(); else t.showWidget(); } }, [open]);

  const submit = async (value) => {
    const v = (value ?? text).trim();
    if (!v || busy) return;
    setBusy(true); setErr('');
    try { await send(v); setText(''); inputRef.current?.focus(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const mailCopy = async () => {
    setNote(''); setErr('');
    try { await emailTranscript(); setNote('A copy of this chat is on its way to your inbox.'); } catch (e) { setErr(e.message); }
  };

  return (
    <>
      {open && (
        <div className="fixed z-[10000] inset-0 sm:inset-auto sm:bottom-28 sm:right-6 sm:w-[380px] sm:h-[560px] bg-[#f6f7f5] sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden ld-chat-pop border border-gray-200" data-testid="live-chat-panel">
          <div className="bg-[#0f2a17] text-white px-5 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center shrink-0"><MessageSquareText size={20} /></div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">Land Drafters Live Chat</p>
              <p className="text-xs text-green-200/80 flex items-center gap-1.5" data-testid="live-chat-status">
                <span className={`w-2 h-2 rounded-full ${meta.admin_online ? 'bg-green-400 animate-pulse' : 'bg-amber-300'}`} />
                {meta.admin_online ? 'Team online now' : office && !office.open ? `Outside office hours · AI replies now, team from ${office.next_open}` : 'Instant replies · team alerted on mobile'}
              </p>
            </div>
            {started && <button onClick={mailCopy} title="Email me a copy of this chat" className="p-1.5 rounded-full hover:bg-white/10 transition-colors" aria-label="Email me a copy" data-testid="live-chat-transcript-btn"><Mail size={17} /></button>}
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/10 transition-colors" aria-label="Close chat" data-testid="live-chat-close"><X size={18} /></button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="live-chat-messages">
            <ChatBubble m={{ id: 'hello', sender: 'greeting', text: 'Hi there! Need help with survey CAD drafting, Civil 3D, GIS or LiDAR to CAD? Ask us anything — you will get an answer in seconds.' }} />
            {!started && <PreChatForm onStart={({ text: t, ...details }) => send(t, details)} />}
            {messages.map((m) => <ChatBubble key={m.id} m={m} fileHref={fileHref} />)}
            {waiting && <TypingNote />}
            {showCard && <ContactCard onSave={saveDetails} onClose={() => setHideCard(true)} />}
            <div ref={endRef} />
          </div>

          {started && <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="border-t border-gray-200 bg-white p-3 flex items-end gap-2">
            <AttachButton url={uploadUrl} extra={{ token: session?.token }} onUploaded={addMessage} onError={setErr} className="w-10 h-10 rounded-xl text-gray-500 hover:text-green-700 hover:bg-gray-100" testId="live-chat-attach" />
            <textarea rows={1} autoFocus ref={inputRef} value={text} onChange={(e) => { setText(e.target.value); typing(e.target.value); }} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
              placeholder="Type your message…" className="flex-1 resize-none text-sm px-3 py-2.5 rounded-xl bg-gray-100 focus:bg-white border border-transparent focus:border-green-600 focus:outline-none max-h-28" data-testid="live-chat-input" />
            <button disabled={busy || !text.trim()} className="w-10 h-10 rounded-xl bg-green-600 text-white flex items-center justify-center hover:bg-green-700 transition-colors disabled:opacity-40" aria-label="Send" data-testid="live-chat-send">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>}
          {started && <p className="text-[10px] text-gray-400 px-4 pb-2 bg-white -mt-1" data-testid="live-chat-typing-note">Our team can see your message as you type, so we can reply faster.</p>}
          {err && <p className="text-xs text-red-600 px-4 pb-2 bg-white" data-testid="live-chat-error">{err}</p>}
          {note && <p className="text-xs text-green-700 px-4 pb-2 bg-white" data-testid="live-chat-note">{note}</p>}
        </div>
      )}

      <button onClick={() => setOpen((o) => !o)} className={`fixed bottom-7 right-6 z-[9999] h-[60px] rounded-full bg-green-600 hover:bg-green-700 text-white shadow-xl items-center gap-2 transition-[transform,background-color] hover:scale-105 ${open ? 'w-[60px] justify-center' : 'pl-5 pr-6'} ${open ? 'hidden sm:flex' : 'flex'}`}
        aria-label={open ? 'Close live chat' : 'Open live chat'} data-testid="live-chat-launcher">
        {open ? <X size={24} /> : <><MessageSquareText size={22} /><span className="text-sm font-semibold hidden sm:inline">Chat with us</span></>}
        {!open && unread > 0 && <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 rounded-full bg-red-500 text-[11px] font-bold flex items-center justify-center" data-testid="live-chat-unread">{unread}</span>}
      </button>
    </>
  );
}
